import React, { forwardRef, useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import classNames from 'classnames';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MONTH_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface DatePickerProps {
  label: string;
  selectedDate: Date | null;
  onChange: (date: Date | null) => void;
  error?: string;
  fullWidth?: boolean;
  showMonthYearPicker?: boolean;
  placeholderText?: string;
  minDate?: Date;
  maxDate?: Date;
}

export const DatePicker = forwardRef<HTMLLabelElement, DatePickerProps>(
  ({
    label,
    selectedDate,
    onChange,
    error,
    fullWidth = true,
    placeholderText = 'Select date...',
    minDate,
    maxDate,
  }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [viewYear, setViewYear] = useState(() =>
      selectedDate ? selectedDate.getFullYear() : new Date().getFullYear()
    );
    const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const justOpenedRef = useRef(false);

    useEffect(() => {
      if (selectedDate) {
        setViewYear(selectedDate.getFullYear());
      }
    }, [selectedDate]);

    useEffect(() => {
      if (!isOpen) return;
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        if (
          triggerRef.current && !triggerRef.current.contains(target) &&
          popupRef.current && !popupRef.current.contains(target)
        ) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleInputClick = useCallback(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPopupPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
      }
      justOpenedRef.current = true;
      setIsOpen(prev => !prev);
      requestAnimationFrame(() => {
        justOpenedRef.current = false;
      });
    }, []);

    const handlePrevYear = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      if (justOpenedRef.current) return;
      setViewYear(y => y - 1);
    }, []);

    const handleNextYear = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      if (justOpenedRef.current) return;
      setViewYear(y => y + 1);
    }, []);

    const handleMonthSelect = useCallback((monthIndex: number) => {
      const newDate = new Date(viewYear, monthIndex, 1);
      onChange(newDate);
      setIsOpen(false);
    }, [viewYear, onChange]);

    const isMonthDisabled = (monthIndex: number): boolean => {
      const monthStart = new Date(viewYear, monthIndex, 1);
      const monthEnd = new Date(viewYear, monthIndex + 1, 0);
      if (minDate && monthEnd < new Date(minDate.getFullYear(), minDate.getMonth(), 1)) return true;
      if (maxDate && monthStart > new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0)) return true;
      return false;
    };

    const isSelected = (monthIndex: number): boolean => {
      if (!selectedDate) return false;
      return selectedDate.getFullYear() === viewYear && selectedDate.getMonth() === monthIndex;
    };

    const displayValue = selectedDate
      ? `${MONTH_FULL[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`
      : '';

    return (
      <label ref={ref} className={classNames('block mb-4', { 'w-full': fullWidth })}>
        <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
        <div>
          <div
            ref={triggerRef}
            onClick={handleInputClick}
            className={classNames(
              "flex items-center justify-between w-full rounded-md border shadow-sm py-2 px-3 cursor-pointer",
              {
                'border-error-300 focus:border-error-500 focus:ring-error-500': error,
                'border-gray-300 hover:border-gray-400': !error,
              }
            )}
          >
            <span className="text-sm whitespace-nowrap truncate">
              {displayValue || placeholderText}
            </span>
            <Calendar size={16} className="text-gray-400 flex-shrink-0 ml-2" />
          </div>

          {isOpen && popupPos && createPortal(
            <div
              ref={popupRef}
              style={{ position: 'absolute', top: popupPos.top, left: popupPos.left, zIndex: 9999 }}
              className="bg-white rounded-md shadow-lg border border-gray-200 p-3 w-[240px]"
            >
              <div className="flex items-center justify-between mb-2">
                <button
                  type="button"
                  onClick={handlePrevYear}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="text-sm font-bold">{viewYear}</span>
                <button
                  type="button"
                  onClick={handleNextYear}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-1">
                {MONTHS.map((month, i) => {
                  const disabled = isMonthDisabled(i);
                  const selected = isSelected(i);
                  return (
                    <button
                      key={month}
                      type="button"
                      disabled={disabled}
                      onClick={() => handleMonthSelect(i)}
                      className={classNames(
                        'py-1.5 px-2 text-sm rounded transition-colors',
                        {
                          'bg-blue-500 text-white font-semibold': selected,
                          'hover:bg-gray-100': !selected && !disabled,
                          'text-gray-300 cursor-not-allowed': disabled,
                          'text-gray-700': !selected && !disabled,
                        }
                      )}
                    >
                      {month}
                    </button>
                  );
                })}
              </div>
            </div>,
            document.body
          )}
        </div>
        {error && <p className="mt-1 text-xs text-error-600">{error}</p>}
      </label>
    );
  }
);

DatePicker.displayName = 'DatePicker';
